import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    makeStyles,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { ArrowLeft20Regular, MoreHorizontal20Regular } from '@fluentui/react-icons';
import { useIsMobile } from '../../app/useMediaQuery';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    back: {
        alignSelf: 'flex-start',
        marginLeft: '-8px',
        color: tokens.colorNeutralForeground3,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
    },
    heading: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: 0,
        flexGrow: 1,
        flexBasis: '280px',
    },
    titleLine: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        minWidth: 0,
    },
    title: {
        margin: 0,
        fontSize: tokens.fontSizeHero700,
        lineHeight: tokens.lineHeightHero700,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.02em',
        overflowWrap: 'anywhere',
        '@media (max-width: 767px)': {
            fontSize: tokens.fontSizeBase600,
            lineHeight: tokens.lineHeightBase600,
        },
    },
    subtitle: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        maxWidth: '72ch',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        flexShrink: 0,
        '@media (max-width: 767px)': {
            width: '100%',
            justifyContent: 'flex-start',
        },
    },
    grow: {
        flexGrow: 1,
    },
});

export interface IHeaderAction {
    key: string;
    label: string;
    icon?: React.ReactElement;
    onClick: () => void;
    appearance?: 'primary' | 'secondary' | 'subtle' | 'outline';
    disabled?: boolean;
    /** Shown only in the overflow menu, never as a button. */
    overflowOnly?: boolean;
    /** Icon-only button on desktop (label becomes the tooltip). */
    iconOnly?: boolean;
    /** Puts a divider above this item in the overflow menu. */
    dividerBefore?: boolean;
}

export interface IPageHeaderProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Badges next to the title. */
    badges?: React.ReactNode;
    backTo?: { to: string; label: string };
    actions?: IHeaderAction[];
    /** Custom trailing content, e.g. a search box. Rendered before the buttons. */
    extra?: React.ReactNode;
}

/**
 * Page title block. On phones every non-primary action folds into an
 * overflow menu so the header stays a single tidy row.
 */
const PageHeader: React.FC<IPageHeaderProps> = ({ title, subtitle, badges, backTo, actions = [], extra }) => {
    const styles = useStyles();
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    const primary = actions.filter((a) => a.appearance === 'primary' && !a.overflowOnly);
    const inline = isMobile
        ? []
        : actions.filter((a) => a.appearance !== 'primary' && !a.overflowOnly);
    const overflow = isMobile
        ? actions.filter((a) => a.appearance !== 'primary')
        : actions.filter((a) => a.overflowOnly);

    const renderButton = (action: IHeaderAction): React.ReactNode => {
        const button = (
            <Button
                key={action.key}
                appearance={action.appearance === 'primary' ? 'primary' : (action.appearance ?? 'secondary')}
                icon={action.icon}
                disabled={action.disabled}
                onClick={action.onClick}
                aria-label={action.iconOnly ? action.label : undefined}
            >
                {action.iconOnly && !isMobile ? undefined : action.label}
            </Button>
        );
        if (action.iconOnly && !isMobile) {
            return (
                <Tooltip key={action.key} content={action.label} relationship="label" withArrow>
                    {button}
                </Tooltip>
            );
        }
        return button;
    };

    return (
        <header className={styles.root}>
            {backTo && (
                <Button
                    appearance="subtle"
                    size="small"
                    icon={<ArrowLeft20Regular />}
                    className={styles.back}
                    onClick={() => navigate(backTo.to)}
                >
                    {backTo.label}
                </Button>
            )}

            <div className={styles.row}>
                <div className={styles.heading}>
                    <div className={styles.titleLine}>
                        <Text as="h1" className={styles.title}>{title}</Text>
                        {badges}
                    </div>
                    {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
                </div>

                {(extra || actions.length > 0) && (
                    <div className={mergeClasses(styles.actions)}>
                        {extra && <div className={isMobile ? styles.grow : undefined}>{extra}</div>}
                        {inline.map(renderButton)}
                        {primary.map(renderButton)}
                        {overflow.length > 0 && (
                            <Menu positioning="below-end">
                                <MenuTrigger disableButtonEnhancement>
                                    <Button appearance="secondary" icon={<MoreHorizontal20Regular />} aria-label="Ещё действия" />
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {overflow.map((action) => (
                                            <React.Fragment key={action.key}>
                                                {action.dividerBefore && <MenuDivider />}
                                                <MenuItem icon={action.icon} disabled={action.disabled} onClick={action.onClick}>
                                                    {action.label}
                                                </MenuItem>
                                            </React.Fragment>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
};

export default PageHeader;
